import { useCallback, useEffect, useState } from "react";
import { Pencil, Save, X, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

// Documentos que el operador puede retocar (plan completo, cada hoja de servicio y la orden de servicio). La versión editada se
// guarda aparte del plan (que se recalcula con cada cambio) y se muestra en su lugar hasta que se vuelve a la versión generada.

export const useDocumentos = (eventId) => {
  const [docs, setDocs] = useState({});
  const [listo, setListo] = useState(false);
  useEffect(() => {
    if (!eventId) return;
    api.get(`/events/${eventId}/documentos`).then((r) => setDocs(Object.fromEntries(r.data.map((d) => [d.tipo, d])))).catch(() => {}).finally(() => setListo(true));
  }, [eventId]);
  const guardar = useCallback(async (tipo, body) => {
    const r = await api.put(`/events/${eventId}/documentos/${encodeURIComponent(tipo)}`, body);
    setDocs((d) => ({ ...d, [tipo]: r.data }));
    return r.data;
  }, [eventId]);
  const quitar = useCallback(async (tipo) => {
    await api.delete(`/events/${eventId}/documentos/${encodeURIComponent(tipo)}`);
    setDocs((d) => { const n = { ...d }; delete n[tipo]; return n; });
  }, [eventId]);
  return { docs, listo, guardar, quitar };
};

// Pone o quita la edición directa sobre la página: todo el texto se puede cambiar; los mapas, iconos y códigos QR, no.
export const editable = (root, on) => {
  if (!root) return;
  const zonas = root.matches?.("[data-editable]") ? [root] : [...root.querySelectorAll("[data-editable]")];
  zonas.forEach((z) => {
    if (on) {
      z.setAttribute("contenteditable", "true");
      z.querySelectorAll(".leaflet-container, svg, img").forEach((x) => x.setAttribute("contenteditable", "false"));
    } else {
      z.removeAttribute("contenteditable");
      z.querySelectorAll("[contenteditable]").forEach((x) => x.removeAttribute("contenteditable"));
    }
  });
};

// Barra de edición (no se imprime): «Editar» → «Guardar» / «Cancelar»; con versión editada, quién y cuándo, aviso si el plan
// ha cambiado después y «Volver a la versión generada».
export const BarraEdicion = ({ canEdit, editando, guardando, editados = [], huella, onEditar, onGuardar, onCancelar, onRestaurar }) => {
  const ultimo = [...editados].sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))[0];
  const desfasado = editados.some((d) => d.huella && huella && d.huella !== huella);
  return (
    <div className="no-print max-w-[900px] mx-auto mt-4 space-y-2" data-testid="barra-edicion">
      {ultimo && !editando && (
        <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-4 py-2 text-sm ${desfasado ? "border-red-600 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="aviso-editado">
          <span className="flex-1 min-w-[14rem]">
            {editados.length > 1 ? `${editados.length} hojas editadas a mano` : "Versión editada a mano"} · {ultimo.updated_by}, {new Date(ultimo.updated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}.
            {desfasado && <b> El plan ha cambiado después de editarla: revísela o vuelva a la versión generada.</b>}
          </span>
          {canEdit && <button onClick={onRestaurar} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-current/30 text-xs font-semibold" data-testid="restaurar-generada"><RotateCcw className="w-3.5 h-3.5" /> Volver a la versión generada</button>}
        </div>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          {!editando ? (
            <button onClick={onEditar} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid="editar-documento">
              <Pencil className="w-4 h-4" /> Editar
            </button>
          ) : (
            <>
              <span className="text-sm text-slate-700 mr-auto">Escriba directamente sobre el documento (los mapas no se editan).</span>
              <button onClick={onCancelar} disabled={guardando} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-300 text-sm" data-testid="cancelar-edicion"><X className="w-4 h-4" /> Cancelar</button>
              <button onClick={onGuardar} disabled={guardando} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold" data-testid="guardar-edicion">
                {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const avisoError = (e) => toast.error(errMsg(e));
